/**
 * Soft nudge banner shown on dog-profile pages to users who have only
 * registered the dog ($25) and have not subscribed to any of the paid
 * tiers. Drives Buyer Pass / Care+ / Pro conversion without being
 * intrusive — dismissible per dog, surfaces only when the user is the
 * dog's owner.
 *
 * Backed by `/api/care/status` + a localStorage dismissal key. We
 * deliberately don't ask the server "is this user paid?" — the soft
 * nudge is fine to render redundantly; it'll just look weird if they
 * dismiss it on every dog.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SparkleIcon as Sparkle, XIcon as XIcon, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";
import { isNativeApp } from "@/lib/nativeBridge";

const dismissalKey = (dogId) => `ak9_upgrade_nudge_dog_${dogId}`;

const PERKS = [
  "Daily Dog Log (meals, walks, weights, vet notes)",
  "Vet-ready PDF summary export",
  "Vaccination & appointment reminders",
  "Photo + video moments timeline",
  "Share-with-sitter live link",
];

export default function UpgradeNudge({ dogId, dogName }) {
  const [hidden, setHidden] = useState(true); // start hidden; reveal only after status check
  // App Store 3.1.1 — no upgrade CTAs inside the native shell.
  const native = isNativeApp();

  useEffect(() => {
    if (native) return;
    if (!dogId) return;
    if (localStorage.getItem(dismissalKey(dogId))) return;
    // /api/care/status reports active=true if Care+/Pro/Elite/Buyer Pass active.
    // We never even render the banner for paid users.
    api.get("/care/status", { silent: true })
      .then((r) => {
        if (!r.data?.active) setHidden(false);
      })
      .catch(() => { /* on error, stay hidden — never accidentally show to paid users */ });
  }, [dogId]);

  if (native || hidden) return null;

  const dismiss = () => {
    localStorage.setItem(dismissalKey(dogId), "1");
    setHidden(true);
  };

  return (
    <div
      className="relative bg-gradient-to-br from-[#FFFBEA] via-[#FFF7D5] to-[#FFEEA8] border border-[#F2E3A1] rounded-2xl p-5 sm:p-6 my-6 shadow-sm"
      data-testid="upgrade-nudge-banner"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-black/5 text-[#888]"
        aria-label="Dismiss"
        data-testid="upgrade-nudge-dismiss"
      >
        <XIcon size={14} />
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#8a6500] mb-1.5 flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" /> Unlock the Dog Log
          </p>
          <h3 className="font-spectral text-xl sm:text-2xl leading-tight">
            Go deeper with {dogName || "your dog"} — try the full toolkit free for 7 days.
          </h3>
          <p className="text-sm text-[#5a4500] mt-2 leading-relaxed">
            Your $25 registration locks in {dogName ? `${dogName}'s` : "your dog's"} permanent pedigree forever. The features below are part of <strong>Armstrong Care+</strong> — track meals, walks, weights, vet visits, and share a live link with your sitter. <strong>Cancel anytime.</strong>
          </p>
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
            {PERKS.map((p) => (
              <li key={p} className="text-xs text-[#5a4500] flex items-start gap-1.5">
                <CheckCircle size={12} weight="fill" className="mt-0.5 text-[#8a6500] shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2 sm:shrink-0">
          <Button asChild className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white" data-testid="upgrade-nudge-cta">
            <Link to="/pricing#care-plus">Start 7-day free trial</Link>
          </Button>
          <Link
            to="/pricing"
            className="text-xs text-[#8a6500] hover:underline"
            data-testid="upgrade-nudge-learn-more"
          >
            See all plans →
          </Link>
        </div>
      </div>
    </div>
  );
}
